import { useState } from "react";
import useDropzoneUpload from "./DropzoneUpload.hook";
import { useUploadDirectToStorage, useGetFileFromStorage } from "src/queries/hooks/storage";
import { selectedAccountIdSelector, selectedAssistantSelector } from "src/stores/selectors/appSelectors";
import useAppStore from "src/stores/appStore";
import { uploadDirectFilesToStorage, prepareBasicFileData, handleDirectFileDownload } from "../FileUpload.utils";
import { ERROR_MESSAGES, ERROR_TYPE, PROGRESS_MODE, DEFAULT_ACCEPTED_TYPES } from "../FileUpload.consts";
import { GENERIC_ERROR_MESSAGES } from "src/components/collections/consts";
import { setDialogErrorSelector } from "src/stores/selectors/collectionsSelectors";

const useFileUploadField = ({
  acceptedFileTypes = DEFAULT_ACCEPTED_TYPES,
  onSubmit
} = {}) => {
  const [errors, setErrors] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileProgress, setFileProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const accountId = useAppStore(selectedAccountIdSelector);
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  const uploadDirectToStorage = useUploadDirectToStorage(accountId, selectedAssistant?.assistantId);
  const getFileFromStorage = useGetFileFromStorage(accountId, selectedAssistant?.assistantId);
  const setDialogError = useAppStore(setDialogErrorSelector);

  const handleDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setIsUploading(true);
    setErrors([]);
    try {
      const basicFiles = acceptedFiles.map(prepareBasicFileData);
      setUploadedFiles(basicFiles);

      // Upload file directly to storage using batch upload
      const result = await uploadDirectFilesToStorage({
        files: acceptedFiles,
        uploadDirectToStorage,
        setFileProgress,
        setErrors,
        progressMode: PROGRESS_MODE.REPLACE
      });

      if (result && result[0] && !result[0].error) {
        const updatedFile = {
          ...basicFiles[0],
          path: result[0].path
        };
        setUploadedFiles([updatedFile]);

        if (onSubmit) {
          onSubmit(updatedFile);
        }
      } else {
        setErrors([{
          fileName: basicFiles[0].file_name,
          error: result?.[0]?.error || ERROR_MESSAGES.UPLOAD_FAILED
        }]);
      }
    } catch (error) {
      setErrors([{ fileName: basicFiles[0].file_name, error: ERROR_MESSAGES.UPLOAD_FAILED }]);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDropRejected = (rejectedFiles) => {
    rejectedFiles.forEach(rejection => {
      const { file } = rejection;
      const normalizedFile = prepareBasicFileData(file);
      const errorMessage = rejection.errors[0].code === ERROR_TYPE.INVALID_TYPE ?
        ERROR_MESSAGES.UNSUPPORTED_FORMAT(acceptedFileTypes) :
        ERROR_MESSAGES.UPLOAD_FAILED;
      setErrors([{ fileName: normalizedFile.name, error: errorMessage }]);
      setUploadedFiles([normalizedFile]);
    });
  };

  const removeFile = () => {
    setUploadedFiles([]);
    setFileProgress({});
    setErrors([]);
    if (onSubmit) onSubmit(null);
  };

  const dropzone = useDropzoneUpload({
    acceptedFileTypes,
    multiple: false,
    onDrop: handleDrop,
    onDropRejected: handleDropRejected
  });

  const getFileError = (file) => {
    const fileName = file.file_name;
    const fileError = errors.find(err => err.fileName === fileName);
    return fileError ? fileError.error : null;
  };

  const handleFileDownload = async (fileData) => {
    if (!fileData) return;

    const filePath = fileData?.path;
    const fileName = fileData?.file_name ?
      `${fileData.file_name}${fileData.file_type ? `.${fileData.file_type}` : ""}` :
      "download";

    try {
      await handleDirectFileDownload({
        getFileMutation: getFileFromStorage,
        filePath,
        fileName
      });
    } catch (error) {
      console.error("Error getting file from storage:", error);
      setDialogError(GENERIC_ERROR_MESSAGES.DOWNLOAD);
    }
  };

  return {
    uploadedFiles,
    isUploading,
    removeFile,
    getFileError,
    handleFileDownload,
    ...dropzone
  };
};

export default useFileUploadField;
